/**
 * React Query hooks for vault operations
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import {
  addVaultMember,
  AddVaultMemberRequest,
  createVault,
  getVault,
  listVaultMembers,
  listVaults,
  removeVaultMember,
  updateVault,
  updateVaultMember,
  UpdateVaultMemberRequest,
  VaultCreate,
  VaultUpdate,
} from "../api/vaults";

/**
 * Hook to list all vaults for current user
 */
export function useVaults() {
  const { getAccessToken } = useAuth();

  return useQuery({
    queryKey: ["vaults"],
    queryFn: async () => {
      const token = await getAccessToken();
      return listVaults(token);
    },
  });
}

/**
 * Hook to get a single vault
 */
export function useVault(vaultId: number | null) {
  const { getAccessToken } = useAuth();

  return useQuery({
    queryKey: ["vaults", vaultId],
    queryFn: async () => {
      if (!vaultId) throw new Error("No vault ID provided");
      const token = await getAccessToken();
      return getVault(vaultId, token);
    },
    enabled: !!vaultId,
  });
}

/**
 * Hook to list vault members
 */
export function useVaultMembers(vaultId: number | null) {
  const { getAccessToken } = useAuth();

  return useQuery({
    queryKey: ["vaults", vaultId, "members"],
    queryFn: async () => {
      if (!vaultId) throw new Error("No vault ID provided");
      const token = await getAccessToken();
      return listVaultMembers(vaultId, token);
    },
    enabled: !!vaultId,
  });
}

/**
 * Hook to create a vault
 */
export function useCreateVault() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: VaultCreate) => {
      const token = await getAccessToken();
      return createVault(data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vaults"] });
    },
  });
}

/**
 * Hook to update a vault
 */
export function useUpdateVault(vaultId: number) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: VaultUpdate) => {
      const token = await getAccessToken();
      return updateVault(vaultId, data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vaults"] });
      queryClient.invalidateQueries({ queryKey: ["vaults", vaultId] });
    },
  });
}

/**
 * Hook to add a vault member
 */
export function useAddVaultMember(vaultId: number) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddVaultMemberRequest) => {
      const token = await getAccessToken();
      return addVaultMember(vaultId, data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vaults", vaultId, "members"] });
    },
  });
}

/**
 * Hook to update a vault member's role
 */
export function useUpdateVaultMember(vaultId: number) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      data,
    }: {
      userId: string;
      data: UpdateVaultMemberRequest;
    }) => {
      const token = await getAccessToken();
      return updateVaultMember(vaultId, userId, data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vaults", vaultId, "members"] });
    },
  });
}

/**
 * Hook to remove a vault member
 */
export function useRemoveVaultMember(vaultId: number) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const token = await getAccessToken();
      return removeVaultMember(vaultId, userId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vaults", vaultId, "members"] });
    },
  });
}
